/**
 * SignalR Service for Real-Time Notifications
 * Handles connection management, reconnection, and event subscriptions
 *
 * Usage:
 * import { signalRService } from './services/signalr';
 *
 * // Start connection
 * await signalRService.start(accessToken);
 *
 * // Subscribe to events
 * signalRService.on('newIssue', (notification) => { ... });
 *
 * // Unsubscribe
 * signalRService.off('newIssue', callback);
 *
 * // Stop connection
 * await signalRService.stop();
 */

import * as signalR from '@microsoft/signalr';

class SignalRService {
  constructor() {
    this.connection = null;
    this.listeners = new Map();
    this.isConnecting = false;
    this.retryCount = 0;
    this.maxRetries = 5;
  }

  /**
   * Start SignalR connection with JWT authentication
   * @param {string} accessToken - JWT token from localStorage
   * @returns {Promise<void>}
   */
  async start(accessToken) {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      console.log('[SignalR] Already connected');
      return;
    }

    if (this.isConnecting) {
      console.log('[SignalR] Connection already in progress');
      return;
    }

    if (!accessToken) {
      // No token - user not authenticated, skip silently
      return;
    }

    this.isConnecting = true;

    try {
      // Determine API URL from environment or default
      // Extract base URL without /api suffix for SignalR hub endpoint
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5200/api';
      const apiUrl = apiBaseUrl.replace('/api', '');

      // Construct the full SignalR hub URL
      const hubUrl = `${apiUrl}/hubs/notifications`;

      // Debug logging to help diagnose connection issues
      console.log('[SignalR] API Base URL:', apiBaseUrl);
      console.log('[SignalR] Hub URL:', hubUrl);

      // Create connection with JWT token
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => accessToken,
          transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
          skipNegotiation: false
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            // Exponential backoff: 0s, 2s, 10s, 30s, 60s
            if (retryContext.elapsedMilliseconds < 60000) {
              return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 60000);
            }
            return null; // Stop retrying after 1 minute
          }
        })
        .configureLogging(signalR.LogLevel.Warning) // Only log warnings and errors, not info
        .build();

      // Setup event handlers
      this.setupEventHandlers();

      // Setup reconnection handlers
      this.setupReconnectionHandlers();

      // Start the connection
      await this.connection.start();
      console.log('[SignalR] Connected successfully');
      this.retryCount = 0;
    } catch (error) {
      // Check if error is authentication-related (401)
      const isAuthError = error.message?.includes('401') || error.message?.includes('Unauthorized');

      if (isAuthError) {
        // Authentication error - don't retry, token is invalid
        this.isConnecting = false;
        // Silently fail - this is expected when token expires or user logs out
        return;
      }

      // For other errors (network issues, server down), retry with backoff
      this.retryCount++;
      if (this.retryCount < this.maxRetries) {
        const retryDelay = Math.min(1000 * Math.pow(2, this.retryCount), 60000);

        setTimeout(() => {
          this.isConnecting = false;
          this.start(accessToken);
        }, retryDelay);
      } else {
        // Max retries reached - stop silently
        this.isConnecting = false;
      }

      // Don't throw error - handle silently
    } finally {
      this.isConnecting = false;
    }
  }

  /**
   * Setup SignalR event handlers for server messages
   */
  setupEventHandlers() {
    if (!this.connection) return;

    // New issue notification
    this.connection.on('NewIssue', (notification) => {
      console.log('[SignalR] Received new issue notification:', notification);
      this.notifyListeners('newIssue', notification);
    });

    // New message notification
    this.connection.on('NewMessage', (notification) => {
      console.log('[SignalR] Received new message notification:', notification);
      this.notifyListeners('newMessage', notification);
    });

    // Issue-specific message (when viewing an issue)
    this.connection.on('NewIssueMessage', (reportId, message) => {
      console.log('[SignalR] Received message for issue', reportId, message);
      this.notifyListeners('newIssueMessage', { reportId, message });
    });

    // Status change notification
    this.connection.on('IssueStatusChanged', (notification) => {
      console.log('[SignalR] Received status change notification:', notification);
      this.notifyListeners('statusChange', notification);
    });

    // General notification
    this.connection.on('ReceiveNotification', (notification) => {
      console.log('[SignalR] Received general notification:', notification);
      this.notifyListeners('notification', notification);
    });

    // Unread count update
    this.connection.on('UnreadCountUpdate', (count) => {
      console.log('[SignalR] Received unread count update:', count);
      this.notifyListeners('UnreadCountUpdate', count);
    });

    // Student transfer notification (received by affected school)
    this.connection.on('StudentTransferNotification', (notification) => {
      console.log('[SignalR] Received student transfer notification:', notification);
      this.notifyListeners('studentTransfer', notification);
    });
  }

  /**
   * Setup reconnection event handlers
   */
  setupReconnectionHandlers() {
    if (!this.connection) return;

    this.connection.onreconnecting((error) => {
      console.warn('[SignalR] Connection lost, reconnecting...', error);
      this.notifyListeners('reconnecting', { error });
    });

    this.connection.onreconnected((connectionId) => {
      console.log('[SignalR] Reconnected successfully with ID:', connectionId);
      this.notifyListeners('reconnected', { connectionId });
      this.retryCount = 0;
    });

    this.connection.onclose((error) => {
      console.error('[SignalR] Connection closed:', error);
      this.notifyListeners('disconnected', { error });
    });
  }

  /**
   * Stop SignalR connection
   * @returns {Promise<void>}
   */
  async stop() {
    if (this.connection) {
      try {
        await this.connection.stop();
        console.log('[SignalR] Connection stopped');
        this.connection = null;
      } catch (error) {
        console.error('[SignalR] Error stopping connection:', error);
      }
    }
  }

  /**
   * Subscribe to a specific event type
   * @param {string} eventType - Event type (newIssue, newMessage, statusChange, etc.)
   * @param {Function} callback - Callback function to execute when event occurs
   */
  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType).push(callback);
  }

  /**
   * Unsubscribe from a specific event
   * @param {string} eventType - Event type
   * @param {Function} callback - Callback function to remove
   */
  off(eventType, callback) {
    if (this.listeners.has(eventType)) {
      const callbacks = this.listeners.get(eventType);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Notify all listeners for a specific event type
   * @param {string} eventType - Event type
   * @param {*} data - Event data
   */
  notifyListeners(eventType, data) {
    if (this.listeners.has(eventType)) {
      this.listeners.get(eventType).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[SignalR] Error in ${eventType} callback:`, error);
        }
      });
    }
  }

  /**
   * Join a specific issue room for real-time updates
   * Call this when user opens an issue detail page
   * @param {number} reportId - Issue report ID
   * @returns {Promise<void>}
   */
  async joinIssueRoom(reportId) {
    if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      try {
        await this.connection.invoke('JoinIssueRoomAsync', reportId);
        console.log(`[SignalR] Joined issue room ${reportId}`);
      } catch (error) {
        console.error(`[SignalR] Error joining issue room ${reportId}:`, error);
      }
    }
  }

  /**
   * Leave a specific issue room
   * Call this when user leaves an issue detail page
   * @param {number} reportId - Issue report ID
   * @returns {Promise<void>}
   */
  async leaveIssueRoom(reportId) {
    if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      try {
        await this.connection.invoke('LeaveIssueRoomAsync', reportId);
        console.log(`[SignalR] Left issue room ${reportId}`);
      } catch (error) {
        console.error(`[SignalR] Error leaving issue room ${reportId}:`, error);
      }
    }
  }

  /**
   * Get current connection state
   * @returns {string|null} Connection state or null if no connection
   */
  getConnectionState() {
    if (!this.connection) return null;

    const states = {
      [signalR.HubConnectionState.Disconnected]: 'Disconnected',
      [signalR.HubConnectionState.Connected]: 'Connected',
      [signalR.HubConnectionState.Connecting]: 'Connecting',
      [signalR.HubConnectionState.Reconnecting]: 'Reconnecting'
    };

    return states[this.connection.state] || 'Unknown';
  }

  /**
   * Check if connection is established
   * @returns {boolean}
   */
  isConnected() {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

// Export singleton instance
export const signalRService = new SignalRService();

// Export SignalR module for state checks
export { signalR };
